import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { CreatableMaintenanceTemplate, UpdatableMaintenanceTemplate } from "@nova/schemas"
import { useSWRNoRevalidate } from "@nova/swr"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { invalidatePilotTrackingQuery } from "features/pilot-tracking/PilotTrackingQueries"
import { useAppSelector } from "integrations/redux/hooks"
import useSWRMutation from "swr/mutation"
import useSWR, { mutate } from "swr"

const baseKey = "maintenance-template"

export const invalidateMaintenanceTemplates = () =>
    Promise.all([
        mutate(
            (key) =>
                Array.isArray(key) &&
                (key[0] === baseKey || key[0] === "maintenance-templates-for-aircraft")
        ),
        invalidatePilotTrackingQuery("maintenance_templates"),
        invalidatePilotTrackingQuery("maintenance_template_aircraft"),
    ])

const createMaintenanceTemplate = ({
    body,
    orgId,
}: {
    body: CreatableMaintenanceTemplate
    orgId: string
}) => {
    return fetchApiGateway("post /org/:orgId/fleet/maintenance/template", {
        body,
        params: { orgId },
    })
}

export const useCreateMaintenanceTemplate = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "create-maintenance-template",
        (key, { arg }: { arg: CreatableMaintenanceTemplate }) =>
            orgId ? createMaintenanceTemplate({ body: arg, orgId }) : undefined,
        { onSuccess: invalidateMaintenanceTemplates }
    )

    return { createMaintenanceTemplate: trigger, isMutating }
}

const getMaintenanceTemplate = ({ templateId, orgId }: { orgId: string; templateId: string }) => {
    return fetchApiGateway("get /org/:orgId/fleet/maintenance/template/:templateId", {
        params: { orgId, templateId },
    })
}

export const useMaintenanceTemplate = (templateId: string) => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { data, isLoading } = useSWRNoRevalidate([baseKey, orgId, templateId], () =>
        orgId ? getMaintenanceTemplate({ templateId, orgId }) : undefined
    )

    return { data, isLoading }
}

export const useMaintenanceTemplatesForAircraft = (aircraftId: string) => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { data, isLoading } = useSWR(
        orgId && aircraftId ? ["maintenance-templates-for-aircraft", aircraftId, orgId] : null,
        () =>
            fetchApiGateway("get /org/:orgId/fleet/aircraft/:aircraftId/maintenance/templates", {
                params: { orgId: orgId!, aircraftId },
            })
    )

    return { templates: data ?? [], isLoading }
}

const updateMaintenanceTemplate = ({
    templateId,
    body,
    orgId,
}: {
    templateId: string
    body: UpdatableMaintenanceTemplate
    orgId: string
}) => {
    return fetchApiGateway("patch /org/:orgId/fleet/maintenance/template/:templateId", {
        body,
        params: { orgId, templateId },
    })
}

export const useUpdateMaintenanceTemplate = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "update-maintenance-template",
        (key, { arg }: { arg: { templateId: string; body: UpdatableMaintenanceTemplate } }) =>
            orgId
                ? updateMaintenanceTemplate({ templateId: arg.templateId, body: arg.body, orgId })
                : undefined,
        { onSuccess: invalidateMaintenanceTemplates }
    )

    return { updateMaintenanceTemplate: trigger, isMutating }
}

const deleteMaintenanceTemplate = ({
    templateId,
    orgId,
}: {
    templateId: string
    orgId: string
}) => {
    return fetchApiGateway("delete /org/:orgId/fleet/maintenance/template/:templateId", {
        params: { orgId, templateId },
    })
}

export const useDeleteMaintenanceTemplate = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "delete-maintenance-template",
        (key, { arg: templateId }: { arg: string }) =>
            orgId ? deleteMaintenanceTemplate({ templateId, orgId }) : undefined,
        { onSuccess: invalidateMaintenanceTemplates }
    )

    return { deleteMaintenanceTemplate: trigger, isMutating }
}

const duplicateMaintenanceTemplate = ({
    templateId,
    orgId,
}: {
    templateId: string
    orgId: string
}) => {
    return fetchApiGateway("post /org/:orgId/fleet/maintenance/template/:templateId/duplicate", {
        params: { orgId, templateId },
    })
}

export const useDuplicateMaintenanceTemplate = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "duplicate-maintenance-template",
        (key, { arg: templateId }: { arg: string }) =>
            orgId ? duplicateMaintenanceTemplate({ templateId, orgId }) : undefined,
        { onSuccess: invalidateMaintenanceTemplates }
    )

    return { duplicateMaintenanceTemplate: trigger, isMutating }
}
